/** The first non-empty string found in a nested errors value */
const firstMessage = (value: unknown, depth = 0): string | null => {
  if (depth > 4 || value === null || value === undefined) {
    return null;
  }
  if (typeof value === 'string') {
    return value.trim() || null;
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = firstMessage(item, depth + 1);
      if (found) {
        return found;
      }
    }
    return null;
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    if (typeof record.msg === 'string' && record.msg.trim()) {
      return record.msg.trim();
    }
    return firstMessage(Object.values(record), depth + 1);
  }
  return null;
};

/**
 * The API's own message from a failed request (an Amplify/axios error), or
 * null when it sent none. Accepts every shape the DMD and Alliance APIs use:
 *
 * - `{ error: "…" }` or `{ message: "…" }`
 * - `{ errors: "…" }` (e.g. Contracting /register/get-uuid)
 * - `{ errors: [{ msg: "…" }] }` (Alliance)
 * - `{ errors: ["…", ["…", { params }]] }` and `{ errors: { field: ["…"] } }`
 *   (Symfony FormHelper::getErrorArray)
 */
export const getApiMessage = (error: unknown): string | null => {
  const data = (error as { response?: { data?: unknown } } | null)?.response
    ?.data as Record<string, unknown> | undefined;
  if (!data || typeof data !== 'object') {
    return null;
  }
  if (typeof data.error === 'string' && data.error.trim()) {
    return data.error.trim();
  }
  return (
    firstMessage(data.errors) ||
    (typeof data.message === 'string' && data.message.trim()
      ? data.message.trim()
      : null)
  );
};
